import {
  Box,
  ButtonProps,
  IconButton,
  Link,
  MenuProps,
  Typography,
} from "@mui/material";
import { Fragment, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Check as CheckIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { LoadingButton } from "~/components/loading-button";
import {
  PolicyAction,
  useAssignPolicyMutation,
  useUnassignPolicyMutation,
} from "~/operations";
import {
  AssignmentOption,
  options,
  PolicyAssignMenu,
} from "./PolicyAssignMenu";
import {
  organizationIdFromMrn,
  OrganizationScope,
  RegionScope,
  ScopeType,
  spaceIdFromMrn,
  SpaceScope,
} from "~/hooks/useScope";
import {
  isOrganizationMrn,
  isSpaceMrn,
  PolicyMrnToURIEncodedId,
  scopeTypeFromMrn,
} from "~/lib/mrn";

export type PolicyAssignButtonProps = {
  scope: SpaceScope | OrganizationScope | RegionScope;
  policyMrn: string;
  assigned: boolean;
  assignedScope?: string | null;
  action?: PolicyAction | null;
  onChange?: (action?: PolicyAction) => void;
  advanced?: boolean;
};

export function PolicyAssignButton({
  scope,
  policyMrn,
  assigned,
  assignedScope,
  action,
  onChange,
  advanced,
}: PolicyAssignButtonProps) {
  const [assignPolicy, assignResult] = useAssignPolicyMutation();
  const [unassignPolicy, unassignResult] = useUnassignPolicyMutation();
  const toggleAssign = assigned ? unassignPolicy : assignPolicy;
  const loading = unassignResult.loading || assignResult.loading;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const selectedOption = options.find((o) => o.action === action);

  const handleToggleClick: ButtonProps["onClick"] = async (e) => {
    await toggleAssign({
      variables: {
        input: {
          assetMrn: scope.mrn,
          policyMrn,
          action,
        },
      },
    });

    onChange?.();
  };

  const handleMenuOpen: ButtonProps["onClick"] = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleMenuItemClick = async (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>,
    option: AssignmentOption,
  ) => {
    setAnchorEl(null);

    if (option.action !== selectedOption?.action) {
      await assignPolicy({
        variables: {
          input: {
            assetMrn: scope.mrn,
            policyMrn,
            action: option.action,
          },
        },
      });

      onChange?.();
    }
  };

  const handleMenuClose: MenuProps["onClose"] = (e) => {
    setAnchorEl(null);
  };

  const scopeText = scope.type === ScopeType.Region ? "platform" : scope.type;
  const buttonText = assigned ? "Added" : `Add to ${scopeText}`;

  const isAssigned = !!assignedScope;
  const isAssignedToActiveScope = isAssigned && assignedScope === scope.mrn;
  const isAssignedToParentScope = isAssigned && !isAssignedToActiveScope;
  const assignedToScopeType = isAssigned
    ? scopeTypeFromMrn(assignedScope)
    : null;
  const assignedToName =
    assignedToScopeType === ScopeType.Region ? "platform" : assignedToScopeType;

  const assignedToPrefix =
    assignedToScopeType === ScopeType.Region ? "" : `/${assignedToScopeType}`;
  const assignedToParams = new URLSearchParams();
  if (assignedScope && isOrganizationMrn(assignedScope)) {
    assignedToParams.set(
      "organizationId",
      organizationIdFromMrn(assignedScope),
    );
  }
  if (assignedScope && isSpaceMrn(assignedScope)) {
    assignedToParams.set("spaceId", spaceIdFromMrn(assignedScope));
  }
  const assignedToPath = `${assignedToPrefix}/policies/${PolicyMrnToURIEncodedId(policyMrn)}`;
  const assignedToHref =
    assignedToScopeType === ScopeType.Region
      ? assignedToPath
      : `${assignedToPath}?${assignedToParams}`;

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        whiteSpace: "nowrap",
        display: "flex",
        gap: 2,
        alignItems: "center",
      }}
    >
      {isAssignedToParentScope && (
        <Typography variant="caption" color="textSecondary">
          Added to{" "}
          <Link
            component={RouterLink}
            to={assignedToHref}
            sx={{ "&:hover": { textDecoration: "underline" } }}
          >
            {assignedToName}
          </Link>
        </Typography>
      )}
      <LoadingButton
        variant="outlined"
        color={assigned ? "excellent" : "primary"}
        size="small"
        sx={{
          whiteSpace: "nowrap",
        }}
        startIcon={assigned ? <CheckIcon fontSize="inherit" /> : undefined}
        onClick={handleToggleClick}
        buttonText={buttonText}
        loading={loading}
        disabled={isAssignedToParentScope}
      />
      {advanced && (
        <Fragment>
          <IconButton
            onClick={handleMenuOpen}
            sx={{ p: 0.25 }}
            color="primary"
            disabled={isAssignedToParentScope}
          >
            <MoreVertIcon />
          </IconButton>
          <PolicyAssignMenu
            {...{
              advanced,
              anchorEl,
              open,
              handleMenuClose,
              selectedOption,
              handleMenuItemClick,
            }}
          />
        </Fragment>
      )}
    </Box>
  );
}
